import { useEffect, useRef, useState } from "react";

import { QRCodeSVG } from "qrcode.react";

import { Button } from "@/components/common";
import { extractErrorMessage } from "@/services/axiosConfig";
import { useMfaConfirm, useMfaSetup } from "@/hooks/useMfa";
import "./Mfa.css";

type Props = {
  /** Refetch /me (or equivalent) once MFA is activated. */
  onActivated: () => void | Promise<void>;
  /** Optional cancel handler — when present, renders a "Cancelar" button. */
  onCancel?: () => void;
};

/**
 * Reusable TOTP enrollment flow: requests a pending secret, shows the QR code
 * + manual secret, then confirms a 6-digit code to activate MFA. Used both in
 * the security settings modal and the mandatory platform-admin gate.
 */
export function MfaSetupFlow({ onActivated, onCancel }: Props) {
  const setup = useMfaSetup();
  const confirm = useMfaConfirm();

  const [secret, setSecret] = useState<string | null>(null);
  const [otpauthUri, setOtpauthUri] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const startedRef = useRef(false);

  // Gera o secret pendente ao montar. IMPORTANTE: dispara UMA única vez.
  // Cada POST /mfa/setup regenera o secret no servidor; em React StrictMode (dev)
  // o effect roda 2x, então sem o guard teríamos 2 chamadas concorrentes e o QR
  // exibido ficaria fora de sync com o secret salvo — o 1º código sempre falharia
  // (o famoso "só funciona depois do hard-reload").
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    setup
      .mutateAsync()
      .then((res) => {
        setSecret(res.secret);
        setOtpauthUri(res.otpauth_uri);
      })
      .catch((err) => setError(extractErrorMessage(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onConfirm() {
    setError(null);
    try {
      await confirm.mutateAsync(code.trim());
      await onActivated();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }

  return (
    <div className="mfa-setup">
      <p className="mfa-step-text">
        Escaneie o QR code abaixo com seu aplicativo autenticador (Google Authenticator, Authy,
        1Password etc.) e digite o código gerado para ativar a autenticação em dois fatores.
      </p>

      {error && <div className="login-error">{error}</div>}

      {otpauthUri ? (
        <>
          <div className="mfa-qr">
            <QRCodeSVG value={otpauthUri} size={176} />
          </div>

          {secret && (
            <div className="mfa-secret">
              <span className="mfa-secret-label">Ou insira esta chave manualmente:</span>
              <div className="mfa-secret-row">
                <code className="mfa-secret-code">{secret}</code>
              </div>
            </div>
          )}

          <input
            className="mfa-otp-input"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            placeholder="Código de 6 dígitos"
            autoComplete="one-time-code"
            autoFocus
          />

          <div className="mfa-actions">
            {onCancel && (
              <Button variant="ghost" onClick={onCancel} disabled={confirm.isPending}>
                Cancelar
              </Button>
            )}
            <Button
              variant="primary"
              onClick={onConfirm}
              disabled={confirm.isPending || code.length !== 6}
            >
              {confirm.isPending ? "Ativando…" : "Ativar"}
            </Button>
          </div>
        </>
      ) : (
        <p className="mfa-step-text">{setup.isPending ? "Gerando chave…" : "Carregando…"}</p>
      )}
    </div>
  );
}
